import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@omni/ui/components/Button/button.component";
import { Fragment, ReactNode } from "react";

import { PasswordField } from "@/components/Form/RHF/FormFields/PasswordField/passwordField.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";

import {
    ConfirmGuestFormValidationSchema,
    ConfirmGuestFormValues
} from "./confirmGuest.schema";

const FORM_ID = "confirmGuestForm";
type ConfirmGuestProps = {
    email: string;
    children: ReactNode;
    submit: (password: string) => void;
    userCreated: boolean;
    link: string;
    isLoading: boolean;
};

const ConfirmGuest = ({
    email,
    children,
    submit,
    link,
    userCreated,
    isLoading
}: ConfirmGuestProps) => {
    const { t } = useGranite();

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            password: ""
        },
        resolver: yupResolver(ConfirmGuestFormValidationSchema)
    });

    const onFormSubmit = ({ password }: ConfirmGuestFormValues) => {
        submit(password);
    };

    return (
        <div>
            <h3>{t("confirmGuestForm.title.createAccount", email)}</h3>
            <hr />
            {!userCreated && (
                <Fragment>
                    {children}
                    <FormInfoProvider
                        formId={FORM_ID}
                        withNeverbounce={false}
                    >
                        {() => (
                            <OmniFormContextProvider {...methods}>
                                <form
                                    id={FORM_ID}
                                    onSubmit={methods.handleSubmit(
                                        onFormSubmit
                                    )}
                                >
                                    <Row>
                                        <Col
                                            xs={12}
                                            md={7}
                                            lg={8}
                                        >
                                            <PasswordField<ConfirmGuestFormValues>
                                                name="password"
                                                label={t(
                                                    "general.form.password.label"
                                                )}
                                                required
                                                placeholder={t(
                                                    "general.form.password.placeholder"
                                                )}
                                                autoComplete={"new-password"}
                                            />
                                        </Col>
                                        <Col
                                            xs={12}
                                            md={5}
                                            lg={4}
                                            className={
                                                "d-flex align-items-center"
                                            }
                                        >
                                            <div className={"mt-md-3 w-100"}>
                                                <Button
                                                    test="confirmGuest-button-submit"
                                                    type="submit"
                                                    size="md"
                                                    color="success"
                                                    fullWidth
                                                    loading={isLoading}
                                                >
                                                    {t(
                                                        "confirmGuestForm.button.confirm"
                                                    )}
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </form>
                            </OmniFormContextProvider>
                        )}
                    </FormInfoProvider>
                </Fragment>
            )}
            {userCreated && (
                <Fragment>
                    <div>{t("confirmGuest.description.accountCreated")}</div>
                    <a href={link}>{t("confirmGuest.link.goToProfile")}</a>
                </Fragment>
            )}
        </div>
    );
};

export default ConfirmGuest;
